// components/PhoneListUpload.jsx — v201 (2026-06)
//
// Shared UI: real Excel/CSV file upload for a phone-number audience.
// Replaces every free-text "paste numbers" box across the platform.
// Normalization + de-dup + reject-with-reason all happen on the backend
// (SSOT). This component only uploads and displays the transparent report.
import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2, CheckCircle2, XCircle, Copy } from "lucide-react";
import { parsePhoneUpload, PHONE_UPLOAD_ACCEPT } from "../lib/phoneUpload";

export function PhoneListUpload({ onChange, testid = "phone-list-upload" }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [fileName, setFileName] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setBusy(true);
    setReport(null);
    setFileName(file.name);
    try {
      const rep = await parsePhoneUpload(file);
      setReport(rep);
      onChange && onChange(rep.phones || []);
      if ((rep.valid_count || 0) === 0) {
        toast.warning("لا توجد أرقام صالحة في الملف بعد التطبيع");
      } else {
        toast.success(`${rep.valid_count} رقم صالح جاهز للإرسال`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "تعذّر قراءة الملف";
      toast.error("فشل رفع الملف", { description: typeof detail === "string" ? detail : "" });
      onChange && onChange([]);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      <input
        ref={fileRef}
        type="file"
        accept={PHONE_UPLOAD_ACCEPT}
        onChange={handleFile}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      <button
        type="button"
        onClick={() => fileRef.current && fileRef.current.click()}
        disabled={busy}
        data-testid={`${testid}-btn`}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed font-bold text-sm transition-colors disabled:opacity-60"
        style={{ borderColor: "#B45309", color: "#5C2E00", background: "#FFF7ED" }}>
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <UploadCloud className="h-5 w-5" />}
        {busy ? "جارٍ قراءة الملف وتطبيع الأرقام…" : "📤 رفع ملف الأرقام (Excel .xlsx أو CSV .csv)"}
      </button>
      <p className="text-[11px] leading-relaxed" style={{ color: "#78716c" }}>
        يُطبَّع كل رقم تلقائياً: يُعاد الصفر المفقود، تُحوَّل الأرقام العربية الهندية إلى إنجليزية،
        ويُقبل 05… أو 5… أو +966… — يُرفض فقط ما لا يمكن تطبيعه، مع بيان السبب.
      </p>

      {report && (
        <div className="rounded-xl border p-3 space-y-2 text-sm" style={{ borderColor: "#e7e5e4", background: "#fafaf9" }}
             data-testid={`${testid}-report`}>
          {fileName && <div className="text-[11px] opacity-60">الملف: {fileName}</div>}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="الكلي" value={report.total} testid={`${testid}-total`} tone="#57534e" />
            <Stat label="صالح" value={report.valid_count} testid={`${testid}-valid`} tone="#166534" />
            <Stat label="مكرَّر أُزيل" value={report.duplicates_removed} testid={`${testid}-dupes`} tone="#b45309" />
            <Stat label="مرفوض" value={report.rejected_count} testid={`${testid}-rejected`} tone="#b91c1c" />
          </div>

          {(report.rejected || []).length > 0 && (
            <div className="rounded-lg p-2" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}
                 data-testid={`${testid}-rejected-list`}>
              <div className="font-bold text-xs mb-1" style={{ color: "#b91c1c" }}>
                <XCircle className="inline h-3.5 w-3.5 ml-1" />
                أرقام مرفوضة (لن تُرسَل) — السبب لكل رقم:
              </div>
              <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                {report.rejected.map((r, i) => (
                  <li key={i} className="text-[11px] flex justify-between gap-2" data-testid={`${testid}-rejected-${i}`}>
                    <span className="font-mono" style={{ color: "#7f1d1d" }}>{r.raw}</span>
                    <span className="opacity-80" style={{ color: "#991b1b" }}>{r.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(report.valid_count || 0) > 0 && (
            <div className="text-xs flex items-center gap-1" style={{ color: "#166534" }}>
              <CheckCircle2 className="h-3.5 w-3.5" />
              {report.valid_count} رقم صالح فريد جاهز للإرسال.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  return (
    <div className="rounded-lg p-2 text-center" style={{ background: "white", border: "1px solid #e7e5e4" }}>
      <div className="text-lg font-extrabold" style={{ color: tone }} data-testid={testid}>
        {Number(value || 0).toLocaleString("ar")}
      </div>
      <div className="text-[10px] opacity-70">{label}</div>
    </div>
  );
}

export default PhoneListUpload;
